import { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import api from '../api';
import { formatDateTime, formatNumber, formatRelativeTime } from '../utils/formatters';

const RANGE_OPTIONS = ['1h', '6h', '24h', '7d', '30d'];
const MODE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'voice', label: 'Voice' },
  { value: 'video', label: 'Video' },
];

function formatDuration(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'Not recorded';
  if (numeric < 1000) return `${Math.round(numeric)} ms`;
  if (numeric < 60000) return `${(numeric / 1000).toFixed(numeric < 10000 ? 2 : 1)} s`;
  const minutes = Math.floor(numeric / 60000);
  const seconds = Math.round((numeric % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function statusTone(status = '') {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'accepted' || normalized === 'ended') return 'text-[#7ee787] bg-[#12351f]';
  if (normalized === 'ringing') return 'text-[#f5b221] bg-[#312500]';
  if (normalized === 'missed' || normalized === 'declined' || normalized === 'cancelled') {
    return 'text-[#ffb4ab] bg-[#3a1715]';
  }
  return 'text-[#c8c8c8] bg-[#252525]';
}

function qualityTone(metric = {}) {
  if (!metric.samples) return 'text-[#8f8f8f]';
  if (!metric.thresholdMs) return 'text-[#f5b221]';
  if (Number(metric.p90Ms || 0) <= Number(metric.thresholdMs || 0)) return 'text-[#7ee787]';
  if (Number(metric.avgMs || 0) <= Number(metric.thresholdMs || 0)) return 'text-[#f5b221]';
  return 'text-[#ffb4ab]';
}

function stageLabel(value = '') {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function CallAnalytics() {
  const [range, setRange] = useState('24h');
  const [mode, setMode] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ range });
      if (mode !== 'all') params.set('mode', mode);
      const response = await api.get(`/admin/platform/call-analytics?${params.toString()}`);
      setData(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load call analytics.');
    } finally {
      setLoading(false);
    }
  }, [mode, range]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics, refreshKey]);

  const summary = data?.summary || {};
  const messageRealtime = data?.messageRealtime || {};
  const topPhaseMetrics = useMemo(
    () => (data?.phaseMetrics || []).filter((item) => item.key !== 'token_prewarm_lead'),
    [data]
  );
  const prewarmMetric = useMemo(
    () => (data?.phaseMetrics || []).find((item) => item.key === 'token_prewarm_lead'),
    [data]
  );

  function changeRange(nextRange) {
    setRange(nextRange);
  }

  function changeMode(nextMode) {
    setMode(nextMode);
  }

  return (
    <Layout searchPlaceholder="Search call IDs, users, phases...">
      <div className="space-y-6">
        <section className="rounded-lg border border-[#4e4632] bg-[#111111] p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#f5b221]">
                Call Speed Analytics
              </p>
              <h1 className="mt-2 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Voice call connection monitor
              </h1>
              <p className="mt-3 max-w-4xl text-sm leading-6 text-[#c8c8c8]">
                Calling, ringing, answer tap, token prewarm, and LiveKit room connection timing across Shimbirr calls.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => changeRange(option)}
                  className={[
                    'rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-[0.16em] transition',
                    range === option
                      ? 'border-[#f5b221] bg-[#f5b221] text-black'
                      : 'border-[#4e4632] bg-black/30 text-[#d2c5ab] hover:border-[#f5b221]',
                  ].join(' ')}
                >
                  {option}
                </button>
              ))}
              {MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => changeMode(option.value)}
                  className={[
                    'rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-[0.16em] transition',
                    mode === option.value
                      ? 'border-[#d2c5ab] bg-[#d2c5ab] text-black'
                      : 'border-[#4e4632] bg-black/30 text-[#d2c5ab] hover:border-[#d2c5ab]',
                  ].join(' ')}
                >
                  {option.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setRefreshKey((current) => current + 1)}
                className="rounded-lg border border-[#f5b221]/45 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-[#f5b221] transition hover:bg-[#f5b221]/10"
              >
                Refresh
              </button>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-lg border border-red-500/30 bg-red-950/20 px-5 py-4 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-lg border border-[#4e4632] bg-[#070707] p-8 text-sm text-[#c8c8c8]">
            Loading Shimbirr call telemetry...
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {(data?.summaryCards || []).map((card) => (
                <div key={card.key} className="rounded-lg border border-[#4e4632] bg-[#070707] p-5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8f8f8f]">
                      {card.label}
                    </p>
                    <MaterialIcon name="speed" className="text-[#f5b221]" />
                  </div>
                  <p className="mt-4 text-4xl font-black tracking-[-0.04em] text-[#f5b221]">
                    {card.value}
                  </p>
                  <p className="mt-2 text-sm text-[#b4b4b4]">{card.subValue}</p>
                </div>
              ))}
            </section>

            <Panel title="Instant message realtime" icon="bolt">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MiniStat
                  label="Messages"
                  value={formatNumber(messageRealtime.totalMessages || 0)}
                  detail={`${formatNumber(messageRealtime.textMessages || 0)} text`}
                />
                <MiniStat
                  label="Media"
                  value={formatNumber(messageRealtime.mediaMessages || 0)}
                  detail="photos, voice, video, files"
                />
                <MiniStat
                  label="Optimistic ACK"
                  value={`${messageRealtime.optimisticCoverageRate || 0}%`}
                  detail={`${formatNumber(messageRealtime.clientNonceCount || 0)} client nonces`}
                />
                <MiniStat
                  label="Reactions"
                  value={formatNumber(messageRealtime.reactionCount || 0)}
                  detail={`${formatNumber(messageRealtime.editedCount || 0)} edits, ${formatNumber(messageRealtime.removedCount || 0)} removed`}
                />
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {(messageRealtime.kindBreakdown || []).map((item) => (
                  <BreakdownRow
                    key={item.key}
                    label={stageLabel(item.key)}
                    value={item.count}
                    total={messageRealtime.totalMessages}
                  />
                ))}
              </div>
            </Panel>

            <section className="grid gap-5 xl:grid-cols-[1.2fr,0.8fr]">
              <Panel title="Connection funnel" icon="account_tree">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  {(data?.funnel || []).map((step) => (
                    <div key={step.key} className="rounded-lg border border-[#292620] bg-black/45 p-4">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8f8f8f]">
                        {step.label}
                      </p>
                      <p className="mt-3 text-3xl font-black text-white">{formatNumber(step.count)}</p>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#252525]">
                        <div
                          className="h-full bg-[#f5b221]"
                          style={{ width: `${Math.min(100, Number(step.rateFromStarted || 0))}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-[#b4b4b4]">
                        {step.rateFromStarted}% of started, {step.rateFromPrevious}% from previous
                      </p>
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="Outcome mix" icon="donut_large">
                <div className="space-y-3">
                  {(data?.outcomeBreakdown || []).map((item) => (
                    <BreakdownRow
                      key={item.key}
                      label={stageLabel(item.key)}
                      value={item.count}
                      total={summary.totalCalls}
                    />
                  ))}
                </div>
                {prewarmMetric ? (
                  <div className="mt-5 rounded-lg border border-[#292620] bg-black/45 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8f8f8f]">
                      Prewarm lead
                    </p>
                    <p className="mt-2 text-2xl font-black text-[#f5b221]">
                      {formatDuration(prewarmMetric.avgMs)}
                    </p>
                    <p className="mt-1 text-xs text-[#b4b4b4]">
                      Average time token was ready before answer across {formatNumber(prewarmMetric.samples)} calls.
                    </p>
                  </div>
                ) : null}
              </Panel>
            </section>

            <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {topPhaseMetrics.map((metric) => (
                <MetricPanel key={metric.key} metric={metric} />
              ))}
            </section>

            <section className="grid gap-5 xl:grid-cols-[1fr,0.9fr]">
              <Panel title="Recent call timelines" icon="timeline">
                <div className="space-y-4">
                  {(data?.recentCalls || []).length === 0 ? (
                    <EmptyState text="No calls found for this filter." />
                  ) : (
                    (data?.recentCalls || []).slice(0, 12).map((call) => (
                      <CallTimelineCard key={call.id} call={call} />
                    ))
                  )}
                </div>
              </Panel>

              <div className="space-y-5">
                <Panel title="Slowest connected calls" icon="report">
                  <div className="space-y-3">
                    {(data?.slowestCalls || []).length === 0 ? (
                      <EmptyState text="No connected calls in this range." />
                    ) : (
                      (data?.slowestCalls || []).map((call) => (
                        <SlowCallRow key={call.id} call={call} />
                      ))
                    )}
                  </div>
                </Panel>

                <Panel title="Metric events" icon="query_stats">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {(data?.stageBreakdown || []).map((item) => (
                      <BreakdownRow
                        key={item.key}
                        label={stageLabel(item.key)}
                        value={item.count}
                        total={(data?.stageBreakdown || []).reduce((sum, row) => sum + Number(row.count || 0), 0)}
                        compact
                      />
                    ))}
                  </div>
                </Panel>
              </div>
            </section>
          </>
        )}

        {data?.generatedAt ? (
          <p className="text-xs uppercase tracking-[0.18em] text-[#8f8f8f]">
            Snapshot generated {formatRelativeTime(data.generatedAt)}
          </p>
        ) : null}
      </div>
    </Layout>
  );
}

function MiniStat({ label, value, detail }) {
  return (
    <div className="rounded-lg border border-[#292620] bg-black/45 p-4">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8f8f8f]">
        {label}
      </p>
      <p className="mt-3 text-3xl font-black text-[#f5b221]">{value}</p>
      <p className="mt-1 text-xs text-[#b4b4b4]">{detail}</p>
    </div>
  );
}

function Panel({ title, icon, children }) {
  return (
    <section className="rounded-lg border border-[#4e4632] bg-[#070707] p-5">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f5b221] text-black">
          <MaterialIcon name={icon} className="text-xl" filled />
        </div>
        <h2 className="text-xl font-black tracking-[-0.03em] text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function MetricPanel({ metric }) {
  const threshold = Number(metric.thresholdMs || 0);
  const p90 = Number(metric.p90Ms || 0);
  const progress = threshold ? Math.min(100, Math.round((p90 / threshold) * 100)) : 100;

  return (
    <div className="rounded-lg border border-[#4e4632] bg-[#070707] p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-black text-white">{metric.label}</h3>
          <p className="mt-1 text-sm leading-6 text-[#b4b4b4]">{metric.description}</p>
        </div>
        <MaterialIcon name="timer" className={qualityTone(metric)} />
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <SpeedStat label="Avg" value={formatDuration(metric.avgMs)} />
        <SpeedStat label="P50" value={formatDuration(metric.p50Ms)} />
        <SpeedStat label="P90" value={formatDuration(metric.p90Ms)} />
        <SpeedStat label="Max" value={formatDuration(metric.maxMs)} />
      </div>
      {threshold ? (
        <div className="mt-5">
          <div className="flex justify-between text-xs text-[#8f8f8f]">
            <span>{formatNumber(metric.samples)} samples</span>
            <span>Target {formatDuration(threshold)}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#252525]">
            <div
              className={p90 <= threshold ? 'h-full bg-[#7ee787]' : 'h-full bg-[#ff6f61]'}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SpeedStat({ label, value }) {
  return (
    <div className="rounded-lg border border-[#292620] bg-black/45 p-3">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8f8f8f]">{label}</p>
      <p className="mt-2 text-lg font-black text-[#f5b221]">{value}</p>
    </div>
  );
}

function BreakdownRow({ label, value, total, compact = false }) {
  const rate = total ? Math.round((Number(value || 0) / Number(total || 0)) * 1000) / 10 : 0;
  return (
    <div className={compact ? 'rounded-lg bg-black/35 p-3' : 'rounded-lg border border-[#292620] bg-black/45 p-4'}>
      <div className="flex items-center justify-between gap-3">
        <p className="truncate text-sm font-bold text-[#d8d8d8]">{label}</p>
        <p className="text-sm font-black text-[#f5b221]">{formatNumber(value)}</p>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#252525]">
        <div className="h-full bg-[#f5b221]" style={{ width: `${Math.min(100, rate)}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-[#8f8f8f]">{rate}%</p>
    </div>
  );
}

function CallTimelineCard({ call }) {
  const timings = call.timings || {};
  return (
    <article className="rounded-lg border border-[#292620] bg-black/45 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-lg px-2.5 py-1 text-xs font-black uppercase ${statusTone(call.statusBucket)}`}>
              {call.statusBucket}
            </span>
            <span className="rounded-lg bg-[#242424] px-2.5 py-1 text-xs font-black uppercase text-[#d2c5ab]">
              {call.mode}
            </span>
            <span className="text-xs text-[#8f8f8f]">{call.id}</span>
          </div>
          <p className="mt-3 text-sm text-[#d8d8d8]">
            {call.caller?.label || 'Unknown'} to {call.receiver?.label || 'Unknown'}
          </p>
          <p className="mt-1 text-xs text-[#8f8f8f]">
            Started {formatDateTime(call.startedAt)} · {formatNumber(call.eventCount)} metric events
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-right sm:grid-cols-4">
          <MiniTiming label="To ring" value={timings.callToPhoneRingingMs} />
          <MiniTiming label="Ring answer" value={timings.ringToAnswerMs} />
          <MiniTiming label="Accept connect" value={timings.acceptedToRoomConnectedMs} />
          <MiniTiming label="Full connect" value={timings.callToConnectedMs} />
        </div>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {(call.timeline || []).map((item, index) => (
          <div key={`${call.id}:${item.stage}:${index}`} className="min-w-[150px] rounded-lg bg-[#111111] px-3 py-2">
            <p className="truncate text-xs font-bold capitalize text-[#d8d8d8]">{item.label}</p>
            <p className="mt-1 text-xs text-[#f5b221]">{formatDuration(item.elapsedMs)}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

function MiniTiming({ label, value }) {
  return (
    <div className="rounded-lg bg-[#111111] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#8f8f8f]">{label}</p>
      <p className="mt-1 text-sm font-black text-[#f5b221]">{formatDuration(value)}</p>
    </div>
  );
}

function SlowCallRow({ call }) {
  const timings = call.timings || {};
  return (
    <div className="rounded-lg border border-[#292620] bg-black/45 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-white">
            {call.caller?.label || 'Unknown'} to {call.receiver?.label || 'Unknown'}
          </p>
          <p className="mt-1 text-xs text-[#8f8f8f]">
            {call.mode} · {formatDateTime(call.startedAt)}
          </p>
        </div>
        <span className="rounded-lg bg-[#3a1715] px-3 py-1 text-xs font-black uppercase text-[#ffb4ab]">
          {formatDuration(timings.callToConnectedMs)}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <MiniTiming label="Ring" value={timings.callToPhoneRingingMs} />
        <MiniTiming label="Answer" value={timings.ringToAnswerMs} />
        <MiniTiming label="Room" value={timings.acceptedToRoomConnectedMs} />
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-lg border border-dashed border-[#333333] bg-black/35 p-5 text-sm text-[#8f8f8f]">
      {text}
    </div>
  );
}
